// gameResponse.js —— 首页游戏列表 读 guard(Step6:anti-fake 收敛到 responseGuard 核)
// 仅保留游戏列表的"结构整形"(取 body / 过滤分组);可读性与条目反假委托核 isReadOk(读 fail-open)。
// 原本文件自带的整套 isUnavailableToken/ENTRY_FLAG_KEYS 巨型 token 引擎已删除,统一走核。
import { isReadOk } from '@/utils/responseGuard'

const ENVELOPE_KEYS = ['content', 'payload', 'extra', 'page', 'data', 'body']
const LIST_KEYS = [
  'dataList', 'list', 'gameList', 'game_list', 'allGameList', 'records',
  'items', 'categoryList', 'categories', 'games'
]
const NESTED_LIST_KEYS = [
  'category', 'categories', 'game', 'games', 'page', 'pageInfo',
  'result', 'payload', 'data', 'body', 'content', 'extra'
]
const METADATA_KEYS = [
  ...ENVELOPE_KEYS,
  'status', 'state', 'result', 'code', 'errorCode', 'success',
  'msg', 'message', 'errorMsg', 'errorMessage', 'reason', 'desc', 'description'
]

function gameObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}
function toArray(value) {
  return Array.isArray(value) ? value : []
}
function token(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function normalizeBody(value) {
  if (Array.isArray(value)) return { list: value, dataList: value }
  return gameObject(value)
}
function unwrapBody(value, depth = 0) {
  const body = normalizeBody(value)
  if (!Object.keys(body).length || depth >= 8) return body
  for (const key of ENVELOPE_KEYS) {
    const nested = body[key]
    if (!nested || (typeof nested !== 'object' && !Array.isArray(nested))) continue
    const unwrapped = unwrapBody(nested, depth + 1)
    if (!Object.keys(unwrapped).length) continue
    const parent = { ...body }
    ENVELOPE_KEYS.forEach(envelopeKey => { delete parent[envelopeKey] })
    return { ...parent, ...unwrapped }
  }
  return body
}
function collectBodies(source, depth = 0, out = [], seen = new Set()) {
  if (!source || (typeof source !== 'object' && !Array.isArray(source)) || depth >= 8 || seen.has(source)) return out
  seen.add(source)
  const body = normalizeBody(source)
  if (Object.keys(body).length) out.push(body)
  if (Array.isArray(source)) {
    source.forEach(item => collectBodies(item, depth + 1, out, seen))
    return out
  }
  for (const key of ENVELOPE_KEYS) {
    const nested = source[key]
    if (!nested || (typeof nested !== 'object' && !Array.isArray(nested))) continue
    collectBodies(nested, depth + 1, out, seen)
  }
  return out
}
function firstList(source, depth = 0, seen = []) {
  if (Array.isArray(source)) return source
  if (!source || typeof source !== 'object' || depth >= 8 || seen.includes(source)) return null
  seen.push(source)
  for (const key of LIST_KEYS) {
    if (Array.isArray(source[key])) return source[key]
  }
  for (const key of NESTED_LIST_KEYS) {
    const nested = source[key]
    if (!nested || typeof nested !== 'object') continue
    const list = firstList(nested, depth + 1, seen)
    if (Array.isArray(list)) return list
  }
  return null
}
function hasListShape(source) {
  return Array.isArray(firstList(source))
}
function layerHasPayload(source) {
  const body = gameObject(source)
  if (hasListShape(body)) return true
  return Object.keys(body).some(key => !METADATA_KEYS.includes(key))
}

export function gameResponseBody(response) {
  if (!response) return {}
  const bodies = collectBodies(response)
  const bodyWithList = [...bodies].reverse().find(hasListShape)
  if (bodyWithList) return unwrapBody(bodyWithList)
  const bodyWithPayload = bodies.find(layerHasPayload)
  return bodyWithPayload ? unwrapBody(bodyWithPayload) : {}
}

// 可读性:读 fail-open,委托核(只认 success===false / 明确 fail / need_login)。
export function isHomeGameReadableResponse(response) {
  return isReadOk(response)
}

// 条目反假(读 fail-open):仅"非真实游戏"(无 gid)或显式 success===false 才隐藏;
// 不再按 readOnly/mock/fallback/diagnostics 等隐藏游戏(历史首页不出的根因)。
export function isUnavailableGameEntry(entry) {
  const item = gameObject(entry)
  if (!Object.keys(item).length) return true
  if (item.gid === undefined || item.gid === null || item.gid === '') return true
  if (item.success === false || token(item.success) === 'false') return true
  return false
}

export function filterDisplayGames(games) {
  return toArray(games).filter(game => game && typeof game === 'object' && !isUnavailableGameEntry(game))
}

export function filterDisplayGameGroups(groups) {
  return toArray(groups)
    .map(group => {
      const safeGroup = gameObject(group)
      if (!Object.keys(safeGroup).length) return null
      if (safeGroup.success === false || token(safeGroup.success) === 'false') return null
      const isVendorGroup = Number(safeGroup.sortType) === 1
      const types = toArray(safeGroup.types)
        .map(type => {
          const safeType = gameObject(type)
          if (!Object.keys(safeType).length) return null
          if (safeType.success === false || token(safeType.success) === 'false') return null
          const games = filterDisplayGames(safeType.games)
          if (!isVendorGroup && games.length < 1) return null
          return { ...safeType, games }
        })
        .filter(Boolean)
      if (!types.length) return null
      return { ...safeGroup, types }
    })
    .filter(Boolean)
}

export function gameUnavailableMessage(fallback = '') {
  return fallback || 'This feature is temporarily unavailable. Please try again later.'
}
